import {
  complement,
  groupBy,
  indexBy,
  isEmpty,
  isNil,
  last,
  map,
  pick,
  prop,
  propEq,
  propOr,
  reject,
  sortBy,
  sum,
} from 'ramda';
import {
  Rational,
  Validator,
  blank,
  leftTableJoin,
  maybeRubyArray,
  numberToCurrency,
  rescuableError,
  toRoundedRational,
} from '../Util';
import CalculateCategories from './CalculateCategories';
import {
  CalendarJob,
  CalendarSlotsConfig,
  calculateAvailableSlots,
  generateEmptyCalendarSlots,
} from './CalendarSlots';
import {getItemCategoryId} from './Categories';
import {EMPTY_GENERIC_ITEM_IMAGE, GenericItemImage} from './Common';
import Constants, {PickupPaymentType} from './Constants';
import {ConsultationItem, EMPTY_CONSULTATION_ITEM} from './ConsultationItems';
import {emailAddressesToString} from './Emails';
import {remoovNodeEndpoint} from './EnvVar';
import {Event} from './Events';
import {ItemDescription, ItemDescriptionStatus} from './ItemDescriptions';
import {getItemsVolume, isBlindPickupItem, isValidQuoteItem} from './Items';
import {
  DEFAULT_MARKET,
  Market,
  MarketMap,
  PickupPriceType,
  isValidMarket,
  marketToTimezone,
} from './Markets';
import {Owner, defaultedGetName, isNameFake, ownerHasPhoneEmail, ownerHasPhoneName} from './Owners';
import {
  QuoteDiff,
  calculateQuotesDelta,
  getPickupQuotes,
  getQuoteChanges,
  quotePickupToActualPickup,
} from './QuoteHistory';
import {standardizePhone} from './Sms';
import {iso8601ToTemporal} from './Temporal';
import {
  MaybeOpenInterval,
  Timestamp,
  TimestampInterval,
  Timezone,
  WeekDay,
  addDays,
  addHours,
  differenceInWeeks,
  isAfterOrEqual,
  isBetweenOrEqual,
  isoNow,
  renderDateTime,
  secondsToMinutes,
  setTimestamp,
} from './Time';
import {RouteTimes, Trip} from './Trips';
import {MaybeProcessingTruckItem, TruckItem} from './TruckItems';
import {TRUCK_VOLUME} from './Trucks';
import {User} from './Users';

const {
  PickupEventType,
  PickupEventType: {ACCEPTED_QUOTE, SMS_CONSULTATION, IN_HOME_CONSULTATION},
  PickupCommunicationMethod: {EMAIL, SMS},
  TransportType: {PICK_UP},
  TruckType: {BOX_TRUCK_26},
  ResaleType: {CONSIGNMENT, REJECTED},
  PickupStatus,
  PickupSize,
  PickupStatus: {
    NOT_GOOD,
    LOST,
    OUT_OF_AREA,
    LEAD,
    CALLED_LEAD,
    ONLINE_CONSULTATION,
    CONSULTATION_SCHEDULED,
    CONSULTATION_COMPLETED,
    PICKUP_QUOTE_PREPARED,
    PICKUP_QUOTE_SENT,
    PICKUP_SCHEDULED,
    PICKUP_COMPLETED,
    PICKUP_INVOICED,
    PICKUP_PAID,
    CLOSED,
  },
  Url: {SCHEDULE_PICKUP_URL},
} = Constants;
export const PUBLIC_PICKUP_EVENT_TYPES = [
  PickupEventType.PICKUP_CREATED,
  PickupEventType.PICKUP_UPDATED,
  PickupEventType.ONLINE_CONSULTATION,
  PickupEventType.SMS_CONSULTATION,
  PickupEventType.IN_HOME_CONSULTATION,
  PickupEventType.CUSTOMER_CASHOUT,
  PickupEventType.CONSULTATION_SCHEDULED,
  PickupEventType.CONSULTATION_RESCHEDULED,
  PickupEventType.CONSULTATION_CANCELLED,
  PickupEventType.PICKUP_SCHEDULED,
  PickupEventType.PICKUP_RESCHEDULED,
  PickupEventType.PICKUP_CANCELLED,
  PickupEventType.PICKUP_COMPLETED,
  PickupEventType.ESTIMATE_NOTES_ADDED,
  PickupEventType.REQUESTED_REASSESS,
  PickupEventType.QUESTIONS_SENT,
  PickupEventType.QUESTIONS_ANSWERED,
  PickupEventType.STATUS_CHANGE,
  PickupEventType.DRIVER_REQUESTED_REASSESSMENT,
  PickupEventType.DRIVER_REASSESSMENT_ANSWER,
  PickupEventType.ACCEPTED_QUOTE,
  'sendConsultationEstimate',
  /** @deprecated Was replaced by saveSiteConsultationItems @todo delete on 2026-01-31 */
  'createOnlineConsultation',
  'saveSiteConsultationItems',
  'finishConsultation',
  'sendCurationFinishedEmail',
  'submitPickupSurvey',
  'submitPickupChurnSurvey',
  'referPotentialPartner',
  'frontendFeedback',
  'getItemCategoryFromBlindPickupDescription',
  'autoSaveConsultationItems',
  'requestQuoteReassessment',
  'answerQuoteReassessment',
];

export {PickupStatus};
export const TEMP_PICKUP_ID = 25447;
export const PICKUP_UNPAID = 'Unpaid';
export const PICKUP_INVOICE = 'Invoice';
export const RECYCLE_VOLUMES = [
  ['1', 'Small item / box'],
  ['5', 'Lamp / Art / Large box'],
  ['10', 'TV / End table / Dining chair'],
  ['15', 'Coffee table / Small cabinet'],
  ['20', 'Lounge chair / Console / Rug'],
  ['30', 'Dresser / Desk / Mattress'],
  ['40', 'Loveseat / 6p Dining Table'],
  ['60', '3s Sofa / Dining Set / Hutch'],
  ['80', 'Small Sectional / L-Desk'],
  ['100', 'Large Sectional Sofa'],
];
export const SEND_CURATION_FINISHED_EMAIL_EVENT = 'sendCurationFinishedEmail';
export type PersistentFile = {
  id?: Maybe<number>;
  url: string;
  delete?: Maybe<boolean>;
};
export type PickupQuoteStatus = 'sent' | 'accepted' | 'rejected' | 'waiting';
export type FlatPickup = {
  id: Maybe<number>;
  encrypted_id: Maybe<string>;
  uid: Maybe<string>;
  market: Maybe<string>;
  status: Maybe<string>;
  // Owner
  owner_id: Maybe<number>;
  name: string;
  paypal_email: Maybe<string>;
  phone: Maybe<string>;
  email: Maybe<string>;
  // Referral
  gclid: Maybe<string>;
  referral_code: Maybe<string>;
  referral_discount: Maybe<number>;
  referral_discount_code: Maybe<string>;
  referral_email: Maybe<string>;
  referral_name: Maybe<string>;
  referral_phone: Maybe<string>;
  // Holder
  holder_email: Maybe<string>;
  holder_name: Maybe<string>;
  holder_phone: Maybe<string>;
  // Contact
  call_follow_up_date: Maybe<Timestamp>;
  communication_method: Maybe<string>;
  sms_channel: Maybe<string>;
  call_channel: Maybe<string>;
  follow_up_date: Maybe<Timestamp>;
  // Details
  transport_type: Maybe<string>;
  vehicle: Maybe<string>;
  submit_photos_preference: Maybe<string>;
  zipcode: Maybe<string>;
  address: Maybe<string>;
  city: Maybe<string>;
  full_location: Maybe<string>;
  // Location
  location_ground_floor: Maybe<boolean>;
  location_elevator: Maybe<boolean>;
  location_parking_available: Maybe<boolean>;
  location_parking_location: Maybe<string>;
  location_more_than_50_feet: Maybe<boolean>;
  location_packing_required: Maybe<boolean>;
  location_full_estate: Maybe<boolean>;
  location_additional_items: Maybe<boolean>;
  location_additional_items_more_than_5: Maybe<boolean>;
  location_additional_items_text: Maybe<string>;
  location_other_comments: Maybe<string>;
  location_number_of_floors: Maybe<number>;
  // Logistics
  logistics_answered: Maybe<boolean>;
  logistics_coi_required: Maybe<boolean>;
  logistics_navigation_issues: Maybe<boolean>;
  logistics_navigation_comments: Maybe<string>;
  logistics_other_comments: Maybe<string>;
  pickup_instructions: Maybe<string>;
  route_time_from_warehouse_in_seconds: Maybe<number>;
  route_time_to_warehouse_in_seconds: Maybe<number>;
  // Consultation
  consultation_date: Maybe<Timestamp>;
  consultant_name: Maybe<string>;
  consultation_trips: Maybe<Trip[]>;
  consultation_item_count: Maybe<number>;
  // Timestamps
  created_at: Maybe<Timestamp>;
  updated_at: Maybe<Timestamp>;
  status_updated_at: Maybe<Timestamp>;
  // Estimate
  estimate_dump_fee: Maybe<number>;
  estimate_disassembly_fee: Maybe<number>;
  estimate_special_categories_fee: Maybe<number>;
  estimate_packing_fee: Maybe<number>;
  estimate_moving_fee: Maybe<number>;
  estimate_stairs_fee: Maybe<number>;
  estimate_parking_fee: Maybe<number>;
  estimate_elevator_not_reserved_fee: Maybe<number>;
  estimate_donation_fee: Maybe<number>;
  estimate_invoice_price: Maybe<number>;
  estimate_pickup_size: Maybe<number>;
  estimate_additional_pickup_fee: Maybe<number>;
  estimate_additional_packing_fee: Maybe<number>;
  estimate_additional_stairs_fee: Maybe<number>;
  estimate_additional_parking_fee: Maybe<number>;
  estimate_additional_elevator_not_reserved_fee: Maybe<number>;
  estimate_additional_dump_fee: Maybe<number>;
  estimate_additional_moving_fee: Maybe<number>;
  estimate_dump_fee_range: Maybe<{
    low_fee: Maybe<number>;
    high_fee: Maybe<number>;
  }>;
  estimate_invoice_price_range: Maybe<{
    low_price: Maybe<number>;
    high_price: Maybe<number>;
  }>;
  estimate_additional_disassembly_fee: Maybe<number>;
  estimate_additional_donation_fee: Maybe<number>;
  estimate_additional_special_categories_fee: Maybe<number>;
  estimate_duration_in_minutes: Maybe<number>;
  consultation_estimate_custom_text: Maybe<string>;
  pending_questions: Maybe<boolean>;
  // Actual Pickup
  pickup_date: Maybe<Timestamp>;
  dump_fee: Maybe<number>;
  disassembly_fee: Maybe<number>;
  special_categories_fee: Maybe<number>;
  packing_fee: Maybe<number>;
  moving_fee: Maybe<number>;
  stairs_fee: Maybe<number>;
  parking_fee: Maybe<number>;
  elevator_not_reserved_fee: Maybe<number>;
  donation_fee: Maybe<number>;
  invoice_price: Maybe<number>;
  payment_type: Maybe<PickupPaymentType>;
  pickup_size: Maybe<number>;
  additional_pickup_fee: Maybe<number>;
  additional_packing_fee: Maybe<number>;
  additional_stairs_fee: Maybe<number>;
  additional_parking_fee: Maybe<number>;
  additional_elevator_not_reserved_fee: Maybe<number>;
  additional_dump_fee: Maybe<number>;
  additional_moving_fee: Maybe<number>;
  additional_disassembly_fee: Maybe<number>;
  additional_donation_fee: Maybe<number>;
  additional_special_categories_fee: Maybe<number>;
  tip: Maybe<number>;
  review: Maybe<{[key: string]: string | string[]}>;
  trips: Trip[];
  item_count: Maybe<number>;
  allowed_payment_methods: Maybe<PickupPaymentType[]>;
  price_table: Maybe<PickupPriceType>;
  deprecated_fields: Maybe<Record<string, unknown>>;
  video_consultation_trip: Maybe<Trip>;
  elevator_reserved: Maybe<boolean>;
  fraction_required_stairs: Maybe<number>;
  funnel_flags: Record<string, string>;
  ai_predicted: {
    job_duration_in_minutes?: Maybe<number>;
    cluster?: Maybe<string>;
    pickup_conversion?: Maybe<number>;
  };
  // TODO: delete
  reason: Maybe<string>;
  funnel: Maybe<string>;
  tip_view_at: Maybe<Timestamp>;
  tip_view_sent_at: Maybe<Timestamp>;
  /** @deprecated */
  ai_predicted_job_duration_in_minutes?: Maybe<number>;
  /** @deprecated */
  ai_predicted_cluster?: Maybe<string>;
  /** @deprecated */
  ai_predicted_pickup_conversion?: Maybe<number>;
};

export type SavedFlatPickup = FlatPickup & {id: number; uid: string};
export type Invoice = {
  sent_at: Maybe<string>;
  paid_at: Maybe<string>;
  pickup_size: Maybe<number>;
  invoice_price: Maybe<number>;
  dump_fee: Maybe<number>;
  referral_discount: Maybe<number>;
  disassembly_fee: Maybe<number>;
  packing_fee: Maybe<number>;
  moving_fee?: Maybe<number>;
  stairs_fee: Maybe<number>;
  parking_fee: Maybe<number>;
  elevator_not_reserved_fee: Maybe<number>;
  donation_fee: Maybe<number>;
  additional_pickup_fee: Maybe<number>;
  additional_packing_fee: Maybe<number>;
  additional_stairs_fee: Maybe<number>;
  additional_parking_fee: Maybe<number>;
  additional_elevator_not_reserved_fee: Maybe<number>;
  additional_disassembly_fee: Maybe<number>;
  additional_donation_fee: Maybe<number>;
  additional_dump_fee: Maybe<number>;
  additional_moving_fee?: Maybe<number>;
  tip: Maybe<number>;
  paypal_id: Maybe<string>;
  paypal_number: Maybe<string>;
  id: number;
  pickup_id: Maybe<number>;
  cancelled_at: Maybe<string>;
  created_at: Maybe<string>;
  updated_at: Maybe<string>;
};
export type Pickup = FlatPickup & {
  tip_view_at: Maybe<string>;
  owner: Maybe<Owner>;
  invoice: Maybe<Invoice>;
  // Items
  item_descriptions: ItemDescription[];
  consultation_items: ConsultationItem[];
  // Files
  files: PersistentFile[];
  images: GenericItemImage[];
  generic_item_images: GenericItemImage[];
  //Paypal
  paypal_payment_id?: string;
};
export type SavedPickup = Pickup & {id: number; encrypted_id: string};
export type SavePickupEvent = Event<
  'savePickup',
  Partial<Pickup & {deleted_consultation_item_ids: number[]}>,
  {previousPickup: Pickup | null}
>;
export type ChangeQuoteEvent = Event<
  'savePickup',
  Pickup & {deleted_consultation_item_ids: number[]},
  {previousPickup: Pickup | null}
>;
export type SendConsultationEstimateEvent = Event<
  'sendConsultationEstimate',
  {
    id?: Maybe<number>;
    encrypted_id?: Maybe<string>;
    custom_text?: Maybe<string>;
    cc?: Maybe<string>;
    pickupDurationInMinutes?: Maybe<number>;
  },
  {pickup: Pickup; updatedPickup: Pickup; trips: Trip[]; updatedTrip: Trip}
>;
export type FirstPickupQuoteEvent = Event<
  'firstPickupQuote',
  {pickupId: number; userId: Maybe<number>; extra?: unknown},
  {bucket: string}
>;
export function isFirstPickupQuoteEvent(event: Maybe<Event>): event is FirstPickupQuoteEvent {
  return event?.event_type === 'firstPickupQuote';
}
export function isSavePickupEvent(event: Event): event is SavePickupEvent {
  return event.event_type === 'savePickup';
}
export function isSendConsultationEstimateEvent(
  event: Event,
): event is SendConsultationEstimateEvent {
  return event.event_type === 'sendConsultationEstimate';
}
export function isChangeQuoteEvent(event: Event): event is ChangeQuoteEvent {
  // This key is only used in pickup form, which is through where we wanna track quote changes
  return isSavePickupEvent(event) && !!event.args.deleted_consultation_item_ids;
}
export function getPickupPendingQuestions(events: Event[]): Event[] {
  return events
    .filter(
      // @ts-ignore
      event => event.event_type === PickupEventType.QUESTIONS_SENT && !event.json?.answered,
    )
    .filter(event =>
      [
        // @ts-ignore
        event?.json?.question,
        //@ts-ignore
        ...(event?.json?.items || []).map(prop('question')),
      ].some(Boolean),
    );
}
export function getPickupQuoteStatus(events: Event[]): Maybe<PickupQuoteStatus> {
  const eventTypes = [
    'createOnlineConsultation',
    'saveSiteConsultationItems',
    'finishConsultation',
    ACCEPTED_QUOTE,
    'sendConsultationEstimate',
    PickupEventType.REQUESTED_REASSESS,
    SMS_CONSULTATION,
    IN_HOME_CONSULTATION,
  ];
  const filteredEvents = events
    .filter(complement(propEq('timestamp', null)))
    .filter(x => eventTypes.includes(x.event_type))
    // @ts-expect-error events is not well typed
    .filter(e => !(e.event_type === 'saveSiteConsultationItems' && !e.json?.diffItems)); // ignoring autosaves that don't change the pickup
  const lastEventType = last(sortBy(propOr('', 'timestamp'), filteredEvents))?.event_type;
  if (!lastEventType) return null;
  return (
    (
      {
        sendConsultationEstimate: 'sent',
        [ACCEPTED_QUOTE]: 'accepted',
        [PickupEventType.REQUESTED_REASSESS]: 'waiting',
        createOnlineConsultation: 'waiting',
        saveSiteConsultationItems: 'waiting',
        finishConsultation: 'waiting',
        [SMS_CONSULTATION]: 'waiting',
        [IN_HOME_CONSULTATION]: 'waiting',
      } as {[key: string]: PickupQuoteStatus}
    )[lastEventType] || null
  );
}
export const EMPTY_FLAT_PICKUP: FlatPickup = {
  market: null,
  sms_channel: null,
  call_channel: null,
  address: null,
  call_follow_up_date: null,
  city: null,
  communication_method: EMAIL,
  consultant_name: null,
  consultation_date: null,
  consultation_estimate_custom_text: null,
  consultation_item_count: 0,
  consultation_trips: [],
  created_at: null,
  updated_at: null,
  status_updated_at: null,
  dump_fee: null,
  disassembly_fee: null,
  special_categories_fee: null,
  packing_fee: null,
  moving_fee: null,
  stairs_fee: null,
  parking_fee: null,
  elevator_not_reserved_fee: null,
  donation_fee: null,
  estimate_packing_fee: null,
  estimate_moving_fee: null,
  estimate_stairs_fee: null,
  estimate_parking_fee: null,
  estimate_elevator_not_reserved_fee: null,
  estimate_dump_fee: null,
  estimate_invoice_price: null,
  estimate_pickup_size: null,
  estimate_additional_pickup_fee: null,
  estimate_additional_packing_fee: null,
  estimate_additional_stairs_fee: null,
  estimate_additional_parking_fee: null,
  estimate_additional_elevator_not_reserved_fee: null,
  estimate_additional_dump_fee: null,
  estimate_additional_moving_fee: null,
  estimate_dump_fee_range: null,
  estimate_invoice_price_range: null,
  estimate_additional_disassembly_fee: null,
  estimate_additional_donation_fee: null,
  estimate_additional_special_categories_fee: null,
  estimate_disassembly_fee: null,
  estimate_special_categories_fee: null,
  estimate_donation_fee: null,
  estimate_duration_in_minutes: null,
  follow_up_date: null,
  id: null,
  encrypted_id: null,
  invoice_price: null,
  owner_id: null,
  payment_type: null,
  pickup_date: null,
  pickup_size: null,
  additional_pickup_fee: null,
  additional_packing_fee: null,
  additional_stairs_fee: null,
  additional_parking_fee: null,
  additional_elevator_not_reserved_fee: null,
  additional_dump_fee: null,
  additional_moving_fee: null,
  additional_disassembly_fee: null,
  additional_donation_fee: null,
  additional_special_categories_fee: null,
  referral_code: null,
  referral_discount: null,
  referral_discount_code: null,
  referral_email: null,
  referral_name: null,
  referral_phone: null,
  holder_email: null,
  holder_name: null,
  holder_phone: null,
  submit_photos_preference: null,
  tip: null,
  zipcode: null,
  uid: 'C',
  name: ' (C)',
  paypal_email: null,
  phone: null,
  email: null,
  status: LEAD,
  transport_type: PICK_UP,
  vehicle: BOX_TRUCK_26,
  location_ground_floor: null,
  location_elevator: null,
  location_parking_available: null,
  location_parking_location: null,
  location_more_than_50_feet: null,
  location_packing_required: null,
  location_full_estate: null,
  location_additional_items: null,
  location_additional_items_more_than_5: null,
  location_additional_items_text: null,
  location_other_comments: null,
  location_number_of_floors: null,
  logistics_answered: null,
  logistics_coi_required: null,
  logistics_navigation_issues: null,
  logistics_navigation_comments: null,
  logistics_other_comments: null,
  pickup_instructions: null,
  full_location: null,
  review: null,
  funnel: null,
  reason: null,
  tip_view_at: null,
  tip_view_sent_at: null,
  gclid: null,
  trips: [],
  item_count: 0,
  pending_questions: false,
  allowed_payment_methods: [],
  price_table: null,
  deprecated_fields: null,
  video_consultation_trip: null,
  route_time_from_warehouse_in_seconds: null,
  route_time_to_warehouse_in_seconds: null,
  elevator_reserved: null,
  fraction_required_stairs: null,
  funnel_flags: {},
  ai_predicted: {},
};
export const EMPTY_PICKUP: Pickup = {
  ...EMPTY_FLAT_PICKUP,
  tip_view_at: null,
  item_descriptions: [],
  owner: null,
  generic_item_images: [],
  consultation_items: [],
  files: [],
  images: [],
  invoice: null,
  pending_questions: false,
  trips: [],
};
export function getPickupName({
  id,
  first_name,
  last_name,
  is_vip,
}: {
  id: Maybe<number>;
  first_name: Maybe<string>;
  last_name: Maybe<string>;
  is_vip?: Maybe<boolean>;
}): string {
  return `${defaultedGetName({first_name, last_name})} (${getPickupUid(id)})${is_vip ? ' ⭐' : ''}`;
}
export function getPickupUid(id: Maybe<number>): string {
  return id ? `C${id}` : '';
}
export function pickupEventMessage(pickupEvent: Event, market: Maybe<string>): string {
  const {event_type, json, user, args} = pickupEvent;
  if (!event_type) return '';
  const timezone = marketToTimezone(market);
  const result = {
    [PickupEventType.PICKUP_CREATED]: () => `Pickup created by '${defaultedGetName(user)}'`,
    [PickupEventType.PICKUP_UPDATED]: () => `Pickup updated by '${defaultedGetName(user)}'`,
    [PickupEventType.SMS_CONSULTATION]: () =>
      `SMS Consultation completed by '${defaultedGetName(user)}'`,
    [PickupEventType.ONLINE_CONSULTATION]: () =>
      `Online Consultation completed by '${defaultedGetName(user)}'`,
    [PickupEventType.IN_HOME_CONSULTATION]: () =>
      `In-home Consultation completed by '${defaultedGetName(user)}'`,
    sendTextMessage: () =>
      // @ts-ignore
      `SMS sent to '${maybeRubyArray(args?.to)}' sent by '${defaultedGetName(user)}'`,
    resendTextMessage: () =>
      // @ts-ignore
      `SMS resent to '${maybeRubyArray(args?.to)}' sent by '${defaultedGetName(user)}'`,
    receiveTextMessage: () =>
      // @ts-ignore
      `SMS received from '${args?.from ? standardizePhone(String(args?.from)) : 'unknown'}'`,
    receiveCall: () =>
      // @ts-ignore
      `Call received from '${args?.from ? standardizePhone(String(args?.from)) : 'unknown'}'`,
    [PickupEventType.CUSTOMER_CASHOUT]: () =>
      `Customer requested cashout of '${numberToCurrency(
        // @ts-ignore
        json?.payout_amount,
        // @ts-ignore
      )}'` + `on '${json?.payout_type}'`,
    [PickupEventType.CONSULTATION_SCHEDULED]: () =>
      `Consultation scheduled to '${renderDateTime(
        // @ts-ignore
        json?.consultation_date,
        timezone,
      )}'`,
    [PickupEventType.CONSULTATION_RESCHEDULED]: () =>
      `Consultation rescheduled to '${renderDateTime(
        // @ts-ignore
        json?.consultation_date,
        timezone,
      )}'`,
    [PickupEventType.CONSULTATION_CANCELLED]: () => `Consultation cancelled`,
    [PickupEventType.PICKUP_SCHEDULED]: () =>
      // @ts-ignore
      `Pickup scheduled to '${renderDateTime(json?.pickup_date, timezone)}'`,
    [PickupEventType.PICKUP_RESCHEDULED]: () =>
      // @ts-ignore
      `Pickup rescheduled to '${renderDateTime(json?.pickup_date, timezone)}'`,
    [PickupEventType.PICKUP_CANCELLED]: () => `Pickup cancelled`,
    [PickupEventType.PICKUP_COMPLETED]: () => `Pickup completed by '${defaultedGetName(user)}'`,
    [PickupEventType.NOTES_ADDED]: () => ``,
    // @ts-ignore
    mergePickups: () => `Pickup Merged with '${json?.uid}' by '${defaultedGetName(user)}'`,
    sendCurationFinishedEmail: () => `Curation Finished email sent`,
    [PickupEventType.STATUS_CHANGE]: () =>
      // @ts-ignore
      `Status updated to '${json?.next_status}' by '${defaultedGetName(user)}'`,
    [PickupEventType.ESTIMATE_NOTES_ADDED]: () =>
      `Notes Added to Estimate by '${defaultedGetName(user)}'`,
    [PickupEventType.REQUESTED_REASSESS]: () => `Customer requested reassessment of the quote`,
    [PickupEventType.QUESTIONS_SENT]: () => `Sent estimate questions to customer`,
    [PickupEventType.QUESTIONS_ANSWERED]: () => `Customer answered estimate questions`,
    submitPickupSurvey: () => `Customer submitted pickup survey`,
    submitPickupChurnSurvey: () => `Customer submitted pickup churn survey`,
    [PickupEventType.DRIVER_REQUESTED_REASSESSMENT]: () => `Driver requested item reassessment`,
    [PickupEventType.DRIVER_REASSESSMENT_ANSWER]: () =>
      `Driver requested reassessment was answered`,
    scheduleVideoConsultation: () =>
      `Video Consultation scheduled to '${renderDateTime(
        // @ts-ignore
        args?.video_consultation_date,
        timezone,
      )}' by ${defaultedGetName(user)}`,
    rescheduleVideoConsultation: () =>
      `Video Consultation rescheduled to '${renderDateTime(
        // @ts-ignore
        args?.video_consultation_date,
        timezone,
      )}' by ${defaultedGetName(user)}`,
    cancelVideoConsultation: () => `Video Consultation canceled by ${defaultedGetName(user)}`,
    savePickup: () =>
      // @ts-ignore
      Number.isFinite(parseFloat(args?.tip)) &&
      // @ts-ignore
      Number.isFinite(parseFloat(json?.previousPickup?.tip)) &&
      // @ts-ignore
      parseFloat(args?.tip) !== parseFloat(json?.previousPickup.tip)
        ? `Tip updated from ${numberToCurrency(
            // @ts-ignore
            json?.previousPickup.tip,
            // @ts-ignore
          )} to ${numberToCurrency(args?.tip)} by '${user?.name || 'No Name'}'`
        : null,
    sendEmail: () => {
      // @ts-ignore
      const emailAddressType = emailAddressesToString(args?.email?.to);
      return `Email sent to ${maybeRubyArray(emailAddressType)} with subject '${
        // @ts-ignore
        args?.email?.subject
      }'`;
    },
    resendEmail: () => {
      // @ts-ignore
      const emailAddressType = emailAddressesToString(args?.email?.to);
      return `Email resent to ${maybeRubyArray(emailAddressType)} with subject '${
        // @ts-ignore
        args?.email?.subject
      }'`;
    },
    setPickupExtendedEndOfConsignment: () => {
      // @ts-ignore
      const {extendedEndOfConsignment} = args;
      // @ts-ignore
      const {itemIds} = json;
      return `Consignment period of the items ${itemIds.join(
        ', ',
      )} extended to ${extendedEndOfConsignment}`;
    },
    setPickupEndOfConsignmentAction: () => {
      // @ts-ignore
      const {action} = args;
      // @ts-ignore
      const {itemIds} = json;
      return `End of consigment action of the items ${itemIds.join(', ')} updated to ${action}`;
    },
    cancelPickupSubscription: () => {
      return 'Customer unsubscribed from emails';
    },
    sendOnTheWayNotification: () => 'Driver started trip',
    cancelTrip: () => 'Driver canceled trip',
    sdrWorkflow: () => 'SDR workflow evaluated',
    sendSdrIntroSms: () => 'SDR intro SMS sent to customer',
    sendSdrPersonalEmail: () => 'SDR personal outreach email sent to customer',
    sendSdrOutreachSlack: () => 'SDR outreach Slack notification sent to quote_leads',
    sendSdrFollowUpSlack: () => 'SDR follow-up Slack notification sent to quote_leads',
    offerSdrDiscount: () => 'SDR 10% discount offered to customer',
    endSdrWorkflow: () =>
      // @ts-ignore
      `SDR workflow ended (${json?.reason || 'completed'})`,
    scheduleRequest: () =>
      isScheduleFinishPickupEvent(pickupEvent)
        ? `Finish pickup attempted by '${defaultedGetName(user)}'`
        : null,
  }[event_type];
  // @ts-ignore
  return result && result();
}
export type PickupDetailedEventMessage = ReturnType<typeof pickupDetailedEventMessage>;
export function pickupDetailedEventMessage(
  event: Event,
  allEvents: Event[],
): {title: string; details?: QuoteDiff} | null {
  const {event_type, user} = event;
  if (isChangeQuoteEvent(event)) {
    const details = getQuoteChanges(event);
    return Object.values(details).map(isEmpty).every(Boolean)
      ? null
      : {
          title: `'${defaultedGetName(user)}' made the following changes:`,
          details,
        };
  }
  if (event_type === 'sendConsultationEstimate') {
    const sendConsultationEstimateEvents = allEvents.filter(
      ({event_type}) => event_type === 'sendConsultationEstimate',
    );
    const quoteNumber =
      sendConsultationEstimateEvents.length -
      sendConsultationEstimateEvents.findIndex(e => e.id === event.id);
    return {
      title: `Quote #${quoteNumber} sent by '${defaultedGetName(user)}'`,
    };
  }
  if (isFinishPickupEvent(event)) {
    const lastQuote = last(getPickupQuotes(allEvents)) as SendConsultationEstimateEvent;
    const details = calculateQuotesDelta(
      quotePickupToActualPickup(lastQuote.json.pickup),
      event.args.pickup,
    );
    return Object.values(details).map(isEmpty).every(Boolean)
      ? null
      : {
          title: `'${defaultedGetName(user)}' has finished the pickup:`,
          details,
        };
  }
  return null;
}
export function getDeletedConsultationItemsFromPickup(events: Event[]) {
  const savePickupEvents = events.filter(isSavePickupEvent);
  return savePickupEvents
    .flatMap(({args: newPickup, json: {previousPickup}}) =>
      previousPickup?.consultation_items.filter(pci =>
        newPickup.deleted_consultation_item_ids?.includes(pci.id),
      ),
    )
    .filter(Boolean);
}
export function hasDisassemblyItems(pickup: Maybe<Pickup>): boolean {
  if (!pickup) return false;
  return pickup.consultation_items.some(ci => !!ci.disassembly_time_in_minutes);
}
export function pickupToFlatPickup(pickup: Pickup): FlatPickup {
  return pick(Object.keys(EMPTY_FLAT_PICKUP), pickup);
}
export function pickupFullLocation({
  city,
  address,
  zipcode,
}: {
  city: Maybe<string>;
  address: Maybe<string>;
  zipcode: Maybe<string>;
}): string {
  return [city, address, zipcode]
    .map(x => (x || '').trim())
    .filter(x => x)
    .join(', ');
}
export function validateSendEstimate(pickup: Pickup): Maybe<string> {
  if (![PICKUP_QUOTE_PREPARED, PICKUP_QUOTE_SENT, PICKUP_SCHEDULED].includes(pickup.status))
    return `Invalid status: ${pickup.status}`;
  if (!(pickup.zipcode && pickup.zipcode.match(/(^\d{5}$)|(^\d{5}-\d{4}$)/)))
    return `Invalid zipcode: ${JSON.stringify(pickup.zipcode)}`;
  if (!pickup.estimate_pickup_size || pickup.estimate_pickup_size <= 0)
    return `Invalid estimate pickup size: ${pickup.estimate_pickup_size}`;
  if (!pickup.consultation_items.some(isValidQuoteItem)) return 'Pickup must have at least 1 item.';
  if (
    pickup.consultation_items.some(
      ci =>
        !isBlindPickupItem(ci) &&
        isValidQuoteItem(ci) &&
        (blank(ci.set_volume) || blank(ci.resale_type) || blank(ci.total_quantity)),
    )
  )
    return 'All items must have size, destination and quantity set.';
  return null;
}

export type ItemQuestion = {
  id: number;
  image?: string;
  index?: number;
  question: string;
  answer?: string;
  brand?: string;
};
export type EstimateQuestions = {
  pickup_id?: number;
  id?: string;
  name?: string;
  event_id?: Maybe<number>;
  question: string;
  answer?: string;
  items: ItemQuestion[];
};
export function validateSendEstimateQuestions(
  pickup: Pickup,
  items: ItemQuestion[],
  question: Maybe<string>,
): Maybe<string> {
  const validPickup = [
    CONSULTATION_COMPLETED,
    PICKUP_QUOTE_PREPARED,
    PICKUP_QUOTE_SENT,
    PICKUP_SCHEDULED,
  ].includes(pickup.status);
  if (!validPickup) return 'Invalid status';
  const pickupItems = pickup.consultation_items.filter(isValidQuoteItem);
  const itemsChanged =
    pickupItems.length != items.length ||
    pickupItems.some(i => items.every(q => q.id != i.id && q.index != i.index));
  if (itemsChanged) return 'Pickup was edited';
  if (!question && !items.map(prop('question')).some(Boolean)) return 'No questions made';
  return null;
}
export function extendedPickupStatus(pickup: FlatPickup | Pickup): Maybe<string> {
  const {pending_questions, status} = pickup;
  const statuses = [PICKUP_QUOTE_SENT, PICKUP_QUOTE_PREPARED, CONSULTATION_COMPLETED];
  return pending_questions && statuses.includes(status) ? `${String(status)} - Q` : status;
}
export function pickupStatusNotAfter(status: string): string[] {
  const orderedStatuses = Object.values(PickupStatus).map(x => String(x));
  const index = orderedStatuses.indexOf(status);
  return orderedStatuses.slice(0, index + 1);
}
export function pickupStatusesNotBefore(status: string): string[] {
  const orderedStatuses = Object.values(PickupStatus).map(x => String(x));
  const index = orderedStatuses.indexOf(status);
  return orderedStatuses.slice(index);
}
export const STATUS_ORDER = [
  '',
  NOT_GOOD,
  LOST,
  OUT_OF_AREA,
  LEAD,
  CALLED_LEAD,
  ONLINE_CONSULTATION,
  CONSULTATION_SCHEDULED,
  CONSULTATION_COMPLETED,
  PICKUP_QUOTE_PREPARED,
  PICKUP_QUOTE_SENT,
  PICKUP_SCHEDULED,
  PICKUP_COMPLETED,
  PICKUP_INVOICED,
  PICKUP_PAID,
  CLOSED,
];
export function isStatusEqualOrAfter(st1: Maybe<string>, st2: Maybe<string>) {
  return STATUS_ORDER.indexOf(st1) >= STATUS_ORDER.indexOf(st2);
}
export function isPickupQuoteSent(events: Event[]): boolean {
  return events.some(({event_type}) =>
    [
      'sendConsultationEstimate',
      'firstPickupQuote',
      'secondPickupQuote',
      'thirdPickupQuote',
    ].includes(event_type),
  );
}
export function pickupNextConsultationItemIndex({
  consultation_items,
}: Pick<Pickup, 'consultation_items'>): number {
  return Math.max(0, ...consultation_items.map(ci => ci.index || 0)) + 1;
}
export function shouldMarkPickupLostWhenAllQuoteItemsRemoved({
  status,
  onlyRemovedItems,
  hasValidQuoteItems,
}: {
  status: Maybe<string>;
  onlyRemovedItems: boolean;
  hasValidQuoteItems: boolean;
}): boolean {
  return (
    onlyRemovedItems &&
    !hasValidQuoteItems &&
    [
      ONLINE_CONSULTATION,
      CONSULTATION_COMPLETED,
      PICKUP_QUOTE_PREPARED,
      PICKUP_QUOTE_SENT,
    ].includes(status || '')
  );
}
export function nextStatusAfterConsultation({
  status,
  lastQuoteSentTimestamp,
  isLastSaveAfterQuoteSent = false,
  onlyRemovedItems = false,
  hasValidQuoteItems = true,
  hasNewPhotos = false,
  communicationMethod,
}: {
  status: Maybe<string>;
  lastQuoteSentTimestamp?: Maybe<string>;
  isLastSaveAfterQuoteSent?: boolean;
  onlyRemovedItems?: boolean;
  hasValidQuoteItems?: boolean;
  hasNewPhotos?: boolean;
  communicationMethod: 'sms' | 'email';
}): Maybe<string> {
  if (
    shouldMarkPickupLostWhenAllQuoteItemsRemoved({
      status,
      onlyRemovedItems,
      hasValidQuoteItems,
    })
  ) {
    return LOST;
  }
  if (hasNewPhotos) {
    return pickupStatusNotAfter(ONLINE_CONSULTATION)
      .concat([LOST, PICKUP_QUOTE_PREPARED, PICKUP_QUOTE_SENT, PICKUP_SCHEDULED])
      .includes(status)
      ? CONSULTATION_COMPLETED
      : status;
  }
  if (communicationMethod === SMS) {
    return status;
  }
  return !lastQuoteSentTimestamp
    ? status === PICKUP_QUOTE_PREPARED
      ? PICKUP_QUOTE_PREPARED
      : CONSULTATION_COMPLETED
    : !isLastSaveAfterQuoteSent
    ? PICKUP_QUOTE_SENT
    : onlyRemovedItems && [PICKUP_QUOTE_SENT, PICKUP_QUOTE_PREPARED].includes(status)
    ? PICKUP_QUOTE_PREPARED
    : CONSULTATION_COMPLETED;
}
export function pickupContactEmail(pickup: Pickup | FlatPickup): string {
  const {holder_email} = pickup;
  return holder_email || ('owner' in pickup ? pickup.owner?.email : pickup.email) || '';
}
export function pickupContactPhone(pickup: Pickup | FlatPickup): string {
  const {holder_phone} = pickup;
  return holder_phone || ('owner' in pickup ? pickup.owner?.phone : pickup.phone) || '';
}
export function pickupContactName(pickup: Pickup): string {
  const {owner, holder_name} = pickup;
  return holder_name || defaultedGetName(owner);
}
export function pickupHasFakeName({
  owner,
  name,
  id,
}: Pick<Pickup, 'owner' | 'name' | 'id'>): boolean {
  return (!!owner && ownerHasPhoneName(owner)) || name === `No Name (C${id})` || isNameFake(name);
}
export function pickupHasFakeEmail({owner}: Pickup): boolean {
  return !!owner && ownerHasPhoneEmail(owner);
}
export function notZeroPickupSizes(): string[] {
  return Object.keys(PickupSize).filter(x => x !== '0/1');
}
export function schedulePickupUrl(encrypted_id: string): string {
  return (
    `${remoovNodeEndpoint()}${SCHEDULE_PICKUP_URL}?` + `id=${encodeURIComponent(encrypted_id)}`
  );
}
// Extracted from PickupService.js
// TODO: type this correctly
export function validateConsultationItem(item: ConsultationItem): Record<string, boolean> {
  const width = item.width,
    depth = item.depth,
    height = item.height,
    set_volume = item.set_volume,
    total_quantity = item.total_quantity,
    total_pieces = item.total_pieces,
    item_category_id = item.item_category_id,
    resale_type = item.resale_type;

  return {
    width: !Validator.allowBlank(Validator.positive)(width) && resale_type !== REJECTED,
    depth: !Validator.allowBlank(Validator.positive)(depth) && resale_type !== REJECTED,
    height: !Validator.allowBlank(Validator.positive)(height) && resale_type !== REJECTED,
    set_volume: isBlindPickupItem(item)
      ? !Validator.allowBlank(Validator.positive)(set_volume) && resale_type !== REJECTED
      : !Validator.strictlyPositive(set_volume) && resale_type !== REJECTED,
    total_quantity: !Validator.strictlyPositive(total_quantity) && resale_type !== REJECTED,
    total_pieces: isBlindPickupItem(item)
      ? !Validator.allowBlank(Validator.positive)(total_pieces) && resale_type !== REJECTED
      : !Validator.strictlyPositive(total_pieces) && resale_type !== REJECTED,
    item_category_id: !Validator.strictlyPositive(item_category_id) && resale_type !== REJECTED,
    resale_type: Validator.isNullUndefinedEmptyString(resale_type),
  };
}
export function consultationItemNeedsAttention(item: ConsultationItem): boolean {
  if (item.reassess_status === 'pending') return true;
  const errors = validateConsultationItem(item);
  if (Object.values(errors).some(Boolean)) return true;
  if (item.resale_type === REJECTED || isBlindPickupItem(item)) return false;
  return (
    !item.generic_item_images?.length ||
    !item.item_category ||
    !item.set_volume ||
    !item.total_quantity ||
    !item.total_pieces ||
    !item.resale_type
  );
}
export function sortConsultationItemsByPriority(items: ConsultationItem[]): ConsultationItem[] {
  return [...items].sort((a, b) => {
    const aPriority = consultationItemNeedsAttention(a);
    const bPriority = consultationItemNeedsAttention(b);
    if (aPriority && !bPriority) return -1;
    if (!aPriority && bPriority) return 1;
    return 0;
  });
}
export function validateConsultationItemModal(item: ConsultationItem, categories: []) {
  const purchase_date = item.purchase_date;
  const original_price = item.original_price;
  const barcode = item.barcode;
  const hasPhotos =
    // @ts-ignore should type
    item.generic_item_images?.filter(x => !x.removed)?.length > 0;
  return {
    original_price: !Validator.allowBlank(Validator.positive)(original_price),
    purchase_date: !Validator.allowBlank(Validator.validNatural)(purchase_date),
    barcode: !Validator.allowBlank(Validator.validNatural)(barcode),
    item_category_id:
      item.resale_type !== 'Recycle' &&
      !CalculateCategories.isCategoryValid(item.item_category_id, categories),
    //@ts-ignore
    generic_item_images: !hasPhotos && !item.no_photos,
  };
}
export type PickupSortEnum =
  | 'id_desc'
  | 'id_asc'
  | 'owner_id_desc'
  | 'owner_id_asc'
  | 'status_desc'
  | 'status_asc'
  | 'consultation_date_desc'
  | 'consultation_date_asc'
  | 'pickup_size_desc'
  | 'pickup_size_asc';
export type PickupSearch = Readonly<{
  paginate?: Maybe<{page: number; perPage: number}>;
  look_for?: Maybe<string>;
  sort_by?: Maybe<PickupSortEnum>;
  id?: Maybe<OrArray<number>>;
  owner_id?: Maybe<OrArray<number>>;
  owner_id_not_in?: Maybe<OrArray<number>>;
  encrypted_id?: Maybe<OrArray<string>>;
  market?: Maybe<OrArray<string>>;
  status?: Maybe<OrArray<string>>;
  zipcode?: Maybe<OrArray<string>>;
  referral_discount_code?: Maybe<OrArray<string>>;
  withItemResaleTypes?: Maybe<OrArray<string>>;
  anyPickupDate?: Maybe<MaybeOpenInterval>;
  everyPickupDate?: Maybe<MaybeOpenInterval>;
  lastPickupDate?: Maybe<MaybeOpenInterval>;
  pickup_date?: Maybe<MaybeOpenInterval>;
  created_at?: Maybe<MaybeOpenInterval>;
  consultation_date?: Maybe<MaybeOpenInterval>;
  follow_up_date?: Maybe<MaybeOpenInterval>;
  toBeUploaded?: Maybe<MaybeOpenInterval>;
  withUnsureConsultationItems?: Maybe<boolean>;
  allItemsProcessed?: Maybe<boolean>;
  hasDonatedItemWithoutReceipt?: Maybe<boolean>;
  hasInvoice?: Maybe<boolean>;
  anyItemStored?: Maybe<boolean>;
  user_blocked?: Maybe<boolean>;
  owner_disable_emails?: Maybe<boolean>;
  owner_do_not_send_update?: Maybe<boolean>;
  monthly_from_pickup_date?: Maybe<boolean>;
  status_updated_at?: Maybe<MaybeOpenInterval>;
  call_follow_up_date?: Maybe<MaybeOpenInterval>;
  partner_id?: Maybe<OrArray<number>>;
  events?: Maybe<{event_type: string; exists: boolean} | {event_type: string; exists: boolean}[]>;
  queryString?: Maybe<string>;
  size?: Maybe<OrArray<string>>;
  /** @deprecated */
  markets?: Maybe<string[]>;
  hasDonationReceipts?: Maybe<boolean>;
}>;
export function pickupWasNotScheduled({status}: {status: Maybe<string>}): boolean {
  return [
    LEAD,
    CALLED_LEAD,
    ONLINE_CONSULTATION,
    CONSULTATION_SCHEDULED,
    CONSULTATION_COMPLETED,
    PICKUP_QUOTE_PREPARED,
    PICKUP_QUOTE_SENT,
  ].includes(status);
}
export function consultationItemsCsvToConsultationItems(csv: string[][]): ConsultationItem[] {
  const header = csv[0].map(h => String(h).toLocaleLowerCase());
  const headerMap = {
    public_notes: 'description',
    barcode: 'barcode',
    total_quantity: 'qty',
    external_sku: 'sku',
    item_category: 'category',
    original_price: 'initial price',
    resale_type: 'resale type',
  };
  const missingHeaders = Object.values(headerMap)
    .map(String)
    .filter(h => !header.includes(h.toLocaleLowerCase()));
  if (missingHeaders.some(Boolean))
    throw rescuableError(`Missing Headers: ${missingHeaders.join(', ')}`);
  return (
    csv
      .slice(1)
      // @ts-ignore
      .map(row => map(h => row[header.indexOf(h)], headerMap))
      .map(item => {
        return {
          ...EMPTY_CONSULTATION_ITEM,
          uuid: crypto.randomUUID(),
          ...item,
          // @ts-ignore
          barcode: (item.barcode && parseInt(item.barcode)) || null,
          // @ts-ignore
          public_notes: [item.public_notes, item.item_category].filter(Boolean).join('\n'),
          total_pieces: 1,
          // @ts-ignore
          original_price: coerceOriginalPrice(item.original_price),
        };
      })
  );
  function coerceOriginalPrice(string: string): Maybe<number> {
    const trimmedString = (string || '').trim();
    const parsedInt = parseInt(trimmedString);
    if (parsedInt) return parsedInt;
    const matchedInt = trimmedString.match(/^\$(\d+)/);
    if (matchedInt) return parseInt(matchedInt[1]);
    return null;
  }
}
export function consultationItemsXlsToConsultationItems(csv: unknown[][]): ConsultationItem[] {
  const header = csv[0].map(h => String(h).toLocaleLowerCase());
  const headerMap = {
    barcode: 'barcode',
    item_category: 'category',
    external_sku: 'sku',
    image: 'image',
    original_price: 'initial price',
    public_notes: 'description',
    resale_type: 'resale type',
    total_quantity: 'qty',
    brand: 'brand',
  };
  const missingHeaders = Object.values(headerMap)
    .map(String)
    .filter(h => !header.includes(h.toLocaleLowerCase()));
  if (missingHeaders.some(Boolean))
    throw rescuableError(`Missing Headers: ${missingHeaders.join(', ')}`);
  return (
    csv
      .slice(1)
      // @ts-ignore
      .map(row => map(h => row[header.indexOf(h)], headerMap))
      // @ts-ignore should type
      .map(({image, ...item}) => {
        const imageUrl = image && String(image).startsWith('https://') ? image : null;
        return {
          ...EMPTY_CONSULTATION_ITEM,
          uuid: crypto.randomUUID(),
          ...item,
          // @ts-ignore should type
          barcode: (item.barcode && parseInt(item.barcode)) || null,
          // @ts-ignore should type
          public_notes: [item.public_notes, item.item_category].filter(Boolean).join('\n'),
          item_category_id:
            // @ts-ignore should type
            item.item_category &&
            // @ts-ignore should type
            getItemCategoryId(item.item_category.split(' > ')),
          total_pieces: 1,
          placeholder_image_url: imageUrl,
          generic_item_images: imageUrl
            ? [
                {
                  ...EMPTY_GENERIC_ITEM_IMAGE,
                  url: imageUrl,
                  small_url: imageUrl,
                  big_url: imageUrl,
                },
              ]
            : [],
        };
      })
  );
}
export function pickupWithAnyTripCompleted(pickup: FlatPickup): boolean {
  return (pickup.trips || []).some(t => !t.deleted_at && t.actual_finish);
}
export function uncollectedConsultationItems(
  {consultation_items}: Pickup,
  truck_items: TruckItem[],
): ConsultationItem[] {
  const collectedConsultationItemIds = truck_items
    .map(prop('consultation_item_id'))
    .filter(Boolean);
  return consultation_items
    .filter(complement(prop('remove_from_quote')))
    .filter(Boolean)
    .filter(propEq('resale_type', CONSIGNMENT))
    .filter(({id}) => !collectedConsultationItemIds.includes(id));
}
export function isLargePickup(
  {estimate_pickup_size, estimate_duration_in_minutes, market}: Pickup,
  calendar: CalendarSlotsConfig,
): boolean {
  const thresholdMap: MarketMap<number> = {
    [Market.BAY_AREA]: 0.75,
    [Market.PHOENIX]: 0,
    [Market.MIAMI]: 0,
    [Market.SACRAMENTO]: 0,
    [Market.LOS_ANGELES]: 0,
    [Market.ORANGE_COUNTY]: 0,
  };
  const threshold = thresholdMap[isValidMarket(market) ? market : DEFAULT_MARKET];
  const isLargeSize = estimate_pickup_size ? estimate_pickup_size >= threshold : false;

  const minutesInHour = 60;
  const marketWorkingHours = calendar.endTime - calendar.startTime;
  const isLargeTime = estimate_duration_in_minutes
    ? estimate_duration_in_minutes > marketWorkingHours * minutesInHour
    : false;
  return isLargeSize || isLargeTime;
}
export function isValidItemPickupTime(timestamp: Timestamp): boolean {
  const now = isoNow();
  return (
    isAfterOrEqual(timestamp, addHours(now, 4)) &&
    isAfterOrEqual(addDays(now, 15), timestamp) &&
    isBetweenOrEqual(
      timestamp,
      setTimestamp(timestamp, {hours: 9, minutes: 0, seconds: 0}),
      setTimestamp(timestamp, {hours: 16, minutes: 0, seconds: 0}),
    )
  );
}
export type MergePickups = Event<
  'mergePickups',
  {primary_pickup_id: number; secondary_pickup_id: number; eventId?: number},
  SavedPickup
>;
export type SubmitPickupSurveyEvent = Event<
  'submitPickupSurvey',
  {id: string; survey: {[key: string]: number | null}}
>;
export type SubmitPickupChurnSurveyEvent = Event<
  'submitPickupChurnSurvey',
  {id: string; survey: {[key: string]: {answer: Maybe<string>; other: string}}}
>;
export type SetPickupExtendedEndOfConsignmentEvent = Event<
  'setPickupExtendedEndOfConsignment',
  {pickupId: number; extendedEndOfConsignment: string; notes: string},
  {itemIds: number[]}
>;
export type SetPickupEndOfConsignmentActionEvent = Event<
  'setPickupEndOfConsignmentAction',
  {pickupId: number; action: string},
  {itemIds: number[]}
>;
export type SendCurationFinishedEmailEvent = Event<
  'sendCurationFinishedEmail',
  {pickupId: number; userId?: Maybe<number>; extra?: unknown}
>;
export type FinishPickupEvent = Event<
  'finishPickup',
  {
    user: User;
    trip: Trip;
    pickup: Pickup;
    truck_items: MaybeProcessingTruckItem[];
    preventSendingPickupReceiptEmail?: boolean;
  }
>;
export type ScheduleFinishPickupEvent = Event<
  'scheduleRequest',
  {
    execute: 'finishPickup';
    params: FinishPickupEvent['args'];
  }
>;
export type OngoingPickupEvent = Event<'ongoingPickup', {pickup: Pickup; truckItems: TruckItem[]}>;
export type SendOnTheWayNotificationEvent = Event<
  'sendOnTheWayNotification',
  {
    id?: number;
    actual_start?: string;
  }
>;
export function isFinishPickupEvent(event: Event): event is FinishPickupEvent {
  return event.event_type === 'finishPickup';
}
export function isScheduleFinishPickupEvent(event: Event): event is ScheduleFinishPickupEvent {
  return (
    event.event_type === 'scheduleRequest' &&
    (event.args as ScheduleFinishPickupEvent['args'] | null)?.execute === 'finishPickup'
  );
}
export function isOngoingPickupEvent(event: Event): event is OngoingPickupEvent {
  return event.event_type === 'ongoingPickup';
}
export function isSendOnTheWayNotificationEvent(
  event: Event,
): event is SendOnTheWayNotificationEvent {
  return event.event_type === 'sendOnTheWayNotification';
}

export function calculateTruckSizeAsRationalString(pickup: Pickup): string {
  const [whole, numerator, denominator] = calculateTruckSizeAsRationalArray(pickup);
  return [
    whole > 0 ? String(whole) : null,
    numerator > 0 ? `${numerator}/${denominator}` : null,
    'Truck',
  ]
    .filter(Boolean)
    .join(' ');
}
export function calculateTruckSizeAsRational(pickup: Pickup): number {
  const [whole, numerator, denominator] = calculateTruckSizeAsRationalArray(pickup);
  return whole + numerator / denominator;
}
export function calculateTruckSizeAsPercentage(pickup: Pickup): number {
  return Math.round(calculateTruckRatio(pickup) * 100);
}
function calculateTruckSizeAsRationalArray(pickup: Pickup): Rational {
  return toRoundedRational(calculateTruckRatio(pickup));
}
function calculateTruckRatio(pickup: Pickup): number {
  return calculateTruckSize(pickup) / TRUCK_VOLUME;
}
export function calculateTruckSize(pickup: Pickup): number {
  return getItemsVolume(pickup.consultation_items.filter(isValidQuoteItem));
}
export function rationalToString(rational: Rational): string {
  const [whole, numerator, denominator] = rational;
  return [whole > 0 ? String(whole) : null, numerator > 0 ? `${numerator}/${denominator}` : null]
    .filter(Boolean)
    .join(' ');
}
export function pickupsToSendCurationFinishedEmail(
  pickups: Pickup[],
  timestamp: Timestamp,
  truck_items: TruckItem[],
): Pickup[] {
  return reject(pickup => weeksSincePickup(pickup) > 3, pickups).filter(pickup => {
    const consignmentTruckItems = truck_items
      .filter(propEq('pickup_id', pickup.id))
      .filter(propEq('resale_type', CONSIGNMENT));
    const processedItems = consignmentTruckItems.filter(truckItem => {
      const item = pickup.item_descriptions.find(propEq('id', truckItem.item_description_id));
      return (
        item?.status === ItemDescriptionStatus.EXPORTED ||
        item?.status === ItemDescriptionStatus.HIDDEN
      );
    });
    const percentageProcessed = processedItems.length / consignmentTruckItems.length;
    return (
      (percentageProcessed >= 0.75 && weeksSincePickup(pickup) < 2) ||
      (percentageProcessed >= 0.5 && weeksSincePickup(pickup) >= 2)
    );
  });
  function weeksSincePickup(pickup: Pickup): number {
    const {pickup_date} = pickup;
    if (!pickup_date) return 0;
    return differenceInWeeks(timestamp, pickup_date);
  }
}
export function calculatePickupBookingCalendar({
  interval: {startDate, endDate},
  unavailableIntervals,
  timezone,
  slotConfig,
  estimateDurationInMinutes,
  routeTimes,
  ignoreRestrictions,
  ...rest
}: {
  interval: TimestampInterval;
  unavailableIntervals: CalendarJob[];
  timezone: Timezone;
  slotConfig: CalendarSlotsConfig;
  estimateDurationInMinutes?: Maybe<number>;
  now: Timestamp;
  availableDays: WeekDay[];
  routeTimes?: RouteTimes;
  ignoreNotice?: Maybe<boolean>;
  ignoreRestrictions?: Maybe<boolean>;
}) {
  const emptyCalendarSlots = generateEmptyCalendarSlots({
    slotConfig,
    temporalInterval: {
      startDate: iso8601ToTemporal(startDate, timezone),
      endDate: iso8601ToTemporal(endDate, timezone),
    },
  });
  const jobDurationInMinutes = !isNil(estimateDurationInMinutes)
    ? estimateDurationInMinutes
    : slotConfig.jobDefaultDurationInMinutes;
  const jobAndRouteTimesInMinutes = routeTimes
    ? jobDurationInMinutes +
      secondsToMinutes(
        routeTimes.timeFromDestinationInSeconds + routeTimes.timeToDestinationInSeconds,
      )
    : jobDurationInMinutes;
  return calculateAvailableSlots({
    slotConfig,
    emptyCalendarSlots,
    unavailableIntervals,
    timezone,
    jobEstimateDurationInMinutes: jobAndRouteTimesInMinutes,
    ignoreRestrictions,
    ...rest,
  });
}
export function isPickupCompleted(pickup: Pickup): boolean {
  return !pickupStatusNotAfter(PICKUP_SCHEDULED).concat(LOST).includes(pickup.status);
}
export function getPickupFee(
  pickup: Pick<
    FlatPickup,
    | 'invoice_price'
    | 'packing_fee'
    | 'moving_fee'
    | 'dump_fee'
    | 'disassembly_fee'
    | 'special_categories_fee'
    | 'donation_fee'
    | 'stairs_fee'
    | 'parking_fee'
    | 'elevator_not_reserved_fee'
    | 'additional_pickup_fee'
    | 'additional_packing_fee'
    | 'additional_disassembly_fee'
    | 'additional_donation_fee'
    | 'additional_special_categories_fee'
    | 'additional_stairs_fee'
    | 'additional_parking_fee'
    | 'additional_elevator_not_reserved_fee'
    | 'additional_dump_fee'
    | 'additional_moving_fee'
    | 'tip'
  >,
): number {
  return (
    (pickup.invoice_price || 0) -
    (pickup.packing_fee || 0) -
    (pickup.moving_fee || 0) -
    (pickup.dump_fee || 0) -
    (pickup.disassembly_fee || 0) -
    (pickup.special_categories_fee || 0) -
    (pickup.donation_fee || 0) -
    (pickup.stairs_fee || 0) -
    (pickup.parking_fee || 0) -
    (pickup.elevator_not_reserved_fee || 0) -
    (pickup.additional_pickup_fee || 0) -
    (pickup.additional_packing_fee || 0) -
    (pickup.additional_disassembly_fee || 0) -
    (pickup.additional_donation_fee || 0) -
    (pickup.additional_special_categories_fee || 0) -
    (pickup.additional_stairs_fee || 0) -
    (pickup.additional_parking_fee || 0) -
    (pickup.additional_elevator_not_reserved_fee || 0) -
    (pickup.additional_dump_fee || 0) -
    (pickup.additional_moving_fee || 0) -
    (pickup.tip || 0)
  );
}
type AssessmentItem = ConsultationItem & {
  timestamp: string;
  user: User;
  event_id: number;
};

export function consultationItemEstimateChanged(
  prev: ConsultationItem | undefined,
  item: ConsultationItem,
): boolean {
  const itemKey = item.id ?? item.uuid;
  if (!itemKey) return false;
  if (!item.resale_type || !item.set_volume) return false;
  return (
    prev?.resale_type !== item.resale_type || Number(prev?.set_volume) !== Number(item.set_volume)
  );
}

export function getAssessmentItemsFromSavePickupEvents(
  events: SavePickupEvent[],
): AssessmentItem[] {
  return sortBy(prop('timestamp'), events).flatMap(event => {
    const previousPickup = event.json?.previousPickup;
    if (!previousPickup) return [];
    const prevById = indexBy(
      item => String(item.id ?? item.uuid),
      (previousPickup.consultation_items ?? []).filter(item => item.id ?? item.uuid),
    );
    return (event.args.consultation_items ?? [])
      .filter(item => {
        const key = item.id ?? item.uuid;
        if (!key) return false;
        return consultationItemEstimateChanged(prevById[String(key)], item);
      })
      .map(item => {
        const set_volume = Number(item.set_volume);
        return {
          ...item,
          set_volume,
          timestamp: event.timestamp,
          user: event.user,
          event_id: event.id,
        } as AssessmentItem;
      });
  });
}

export function resolveItemChangedByEditor(
  assessmentItem: AssessmentItem,
  savePickupEvents: SavePickupEvent[],
): User | null {
  const key = assessmentItem.id ?? assessmentItem.uuid;
  if (!key) return null;
  const editsAfterAssessment = sortBy(prop('timestamp'), savePickupEvents)
    .filter(event => event.timestamp > assessmentItem.timestamp)
    .filter(event => {
      const previousPickup = event.json?.previousPickup;
      if (!previousPickup) return false;
      const prevById = indexBy(
        item => String(item.id ?? item.uuid),
        (previousPickup.consultation_items ?? []).filter(item => item.id ?? item.uuid),
      );
      const item = (event.args.consultation_items ?? []).find(
        i => String(i.id ?? i.uuid) === String(key),
      );
      if (!item) return false;
      return consultationItemEstimateChanged(prevById[String(key)], item);
    });

  return last(editsAfterAssessment)?.user ?? null;
}

export function calculateIncorrectItemAssessments({
  quoteItems,
  assessmentItems,
}: {
  quoteItems: (ConsultationItem & {timestamp: string; user: User; event_id: number})[];
  assessmentItems: AssessmentItem[];
}) {
  const groupedAssessments = groupBy<AssessmentItem>(
    entry => `${entry.id}-${entry.user.id}`,
    assessmentItems.filter(prop('resale_type')),
  );
  const lastUserAssessmentItems: AssessmentItem[] = Object.values(groupedAssessments)
    .map((assessments: AssessmentItem[]) => last(sortBy(prop('timestamp'), assessments)))
    .filter(Boolean);
  return leftTableJoin((a, b) => a.id === b.id, lastUserAssessmentItems, quoteItems)
    .filter(({left, right}) => {
      return (
        left.resale_type !== right.resale_type ||
        Number(left.set_volume) !== Number(right.set_volume)
      );
    })
    .map(({left: assessmentItem, right: quoteItem}) => ({
      ...assessmentItem,
      quote_resale_type: quoteItem.resale_type,
      quote_set_volume: quoteItem.set_volume,
      generic_item_images: quoteItem.generic_item_images,
    }));
}
export const SDR_EVENT_TYPES = {
  INTRO_SMS_SENT: 'sendSdrIntroSms',
  PERSONAL_EMAIL_SENT: 'sendSdrPersonalEmail',
  OUTREACH_SLACK_SENT: 'sendSdrOutreachSlack',
  FOLLOW_UP_SLACK_SENT: 'sendSdrFollowUpSlack',
  DISCOUNT_OFFERED: 'offerSdrDiscount',
  WORKFLOW_ENDED: 'endSdrWorkflow',
} as const;

const SDR_MILESTONE_EVENT_TYPES = [
  SDR_EVENT_TYPES.INTRO_SMS_SENT,
  SDR_EVENT_TYPES.PERSONAL_EMAIL_SENT,
  SDR_EVENT_TYPES.OUTREACH_SLACK_SENT,
  SDR_EVENT_TYPES.FOLLOW_UP_SLACK_SENT,
  SDR_EVENT_TYPES.DISCOUNT_OFFERED,
  SDR_EVENT_TYPES.WORKFLOW_ENDED,
] as const;
export const SDR_DISCOUNT_CODE = 'SDR10';
export const SDR_VOLUME_THRESHOLD = PickupSize['1/4'];
export type SdrWorkflowStep =
  | 'not_started'
  | 'awaiting_first_booking'
  | 'awaiting_second_booking'
  | 'completed';
export function isSdrEligiblePickup(pickup: Pickup | FlatPickup): boolean {
  return (pickup.estimate_pickup_size ?? 0) >= SDR_VOLUME_THRESHOLD;
}
export function filterSdrWorkflowEvents(events: Event[]): Event[] {
  const types = Object.values(SDR_EVENT_TYPES);
  return events.filter(e => types.includes(e.event_type));
}
export function findLastQuoteSentEvent(events: Event[]): Maybe<FirstPickupQuoteEvent> {
  const quoteEvents = events.filter(isFirstPickupQuoteEvent);
  return last(sortBy(propOr('', 'timestamp'), quoteEvents));
}
export function findLastQuoteSentTimestamp(events: Event[]): Maybe<string> {
  return findLastQuoteSentEvent(events)?.timestamp;
}
export function lastSdrEventTimestamp(sdrEvents: Event[], eventTypes: string[]): Maybe<string> {
  const matching = sdrEvents.filter(e => eventTypes.includes(e.event_type));
  return last(sortBy(propOr('', 'timestamp'), matching))?.timestamp;
}
export function isPickupScheduled(pickup: Pickup | FlatPickup, events: Event[]): boolean {
  return (
    Boolean(pickup.pickup_date) ||
    isPickupBooked(pickup) ||
    hasPickupEventType(events, PickupEventType.PICKUP_SCHEDULED)
  );
}
export function isPickupBooked(pickup: Pickup | FlatPickup): boolean {
  return isStatusEqualOrAfter(pickup.status, PICKUP_SCHEDULED);
}
export function hasPickupEventType(events: Event[], eventType: string): boolean {
  return events.some(e => e.event_type === eventType);
}
export function isSdrWorkflowEnded(events: Event[], pickup: Pickup | FlatPickup): boolean {
  return (
    hasPickupEventType(events, SDR_EVENT_TYPES.WORKFLOW_ENDED) ||
    isPickupBooked(pickup) ||
    hasPickupEventType(events, PickupEventType.PICKUP_SCHEDULED) ||
    [LOST, NOT_GOOD].includes(pickup.status || '')
  );
}

export function getSdrWorkflowState(sdrEvents: Event[]): SdrWorkflowStep {
  const milestones = sdrEvents
    .filter(e => (SDR_MILESTONE_EVENT_TYPES as readonly string[]).includes(e.event_type))
    .sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
  const lastMilestone = milestones[milestones.length - 1];
  if (!lastMilestone) return 'not_started';
  switch (lastMilestone.event_type) {
    case SDR_EVENT_TYPES.INTRO_SMS_SENT:
      return 'not_started';
    case SDR_EVENT_TYPES.OUTREACH_SLACK_SENT:
    case SDR_EVENT_TYPES.PERSONAL_EMAIL_SENT:
      return 'awaiting_first_booking';
    case SDR_EVENT_TYPES.FOLLOW_UP_SLACK_SENT:
      return 'awaiting_second_booking';
    case SDR_EVENT_TYPES.DISCOUNT_OFFERED:
    case SDR_EVENT_TYPES.WORKFLOW_ENDED:
      return 'completed';
    default:
      return 'not_started';
  }
}

export const PICKUP_CALL_BACK_INVOICE_THRESHOLD = 500;
export function isBlindPickup(pickup: Pickup): boolean {
  return pickup.consultation_items.every(
    item =>
      item.generic_item_images?.every(gii => gii.id === 0) ||
      item.generic_item_images?.length === 0,
  );
}
export type JobDurationModelParams = {
  crew_size: number;
  fraction_required_stairs: number;
  number_of_floors: number;
  parking_available: boolean;
  packing_required: boolean;
  market: string;
  items_count: number;
  items_volume: number;
  disassembly_time_in_minutes: number;
  parking_more_than_50_feet: boolean;
};
export function getJobDurationModelParams(p: Pickup): JobDurationModelParams {
  const crew = p.trips.find(t => !!t.deleted_at)?.crew?.length;
  const lead = 1;
  const items = p.consultation_items.filter(isValidQuoteItem);
  return {
    crew_size: crew ? crew + lead : 2,
    fraction_required_stairs: p.fraction_required_stairs || 1,
    number_of_floors: p.location_number_of_floors || 4,
    parking_available: Boolean(p.location_parking_available),
    packing_required: Boolean(p.location_packing_required),
    market: p.market || DEFAULT_MARKET,
    items_count: sum(items.map(i => i.total_quantity || 0)) || 1,
    items_volume: sum(items.map(i => (i.set_volume || 0) * (i.total_quantity || 0))) || 1,
    disassembly_time_in_minutes: sum(
      p.consultation_items.map(i => (i.disassembly_time_in_minutes || 0) * (i.total_quantity || 0)),
    ),
    parking_more_than_50_feet: Boolean(p.location_more_than_50_feet),
  };
}
